import { useEffect } from "react";

/**
 * useSEO — rewrite head tags client-side for SEO landing pages.
 * Restores previous values when the component unmounts so navigating
 * back to the home (or other pages) doesn't pollute their meta tags.
 *
 * Note: For first-paint SEO, true SSR/SSG would be ideal, but Googlebot
 * executes JS so this approach works in practice (verified via Search Console).
 *
 * @param {object} opts
 * @param {string} opts.title         <title>
 * @param {string} opts.description   <meta name="description">
 * @param {string} [opts.keywords]    <meta name="keywords">
 * @param {string} opts.canonical     <link rel="canonical">
 */
export const useSEO = ({ title, description, keywords, canonical }) => {
  useEffect(() => {
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el && value !== undefined && value !== null) el.setAttribute(attr, value);
    };
    const prev = {
      title: document.title,
      desc: document.querySelector('meta[name="description"]')?.getAttribute("content"),
      keywords: document.querySelector('meta[name="keywords"]')?.getAttribute("content"),
      canonical: document.querySelector('link[rel="canonical"]')?.getAttribute("href"),
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.querySelector('meta[property="og:description"]')?.getAttribute("content"),
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute("content"),
    };

    if (title) document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="keywords"]', "content", keywords);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonical);

    return () => {
      document.title = prev.title;
      setMeta('meta[name="description"]', "content", prev.desc);
      setMeta('meta[name="keywords"]', "content", prev.keywords);
      setMeta('link[rel="canonical"]', "href", prev.canonical);
      setMeta('meta[property="og:title"]', "content", prev.ogTitle);
      setMeta('meta[property="og:description"]', "content", prev.ogDesc);
      setMeta('meta[property="og:url"]', "content", prev.ogUrl);
    };
  }, [title, description, keywords, canonical]);
};
